import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon, MapPinIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const COMPANIES = [{
  name: 'GSK',
  logo: 'https://cdn.magicpatterns.com/uploads/aCqnqXncNjk1xsdUQg5TFB/Screenshot_2026-08-20_at_21.22.24.png'
}, {
  name: 'LEO Pharma',
  logo: 'https://cdn.magicpatterns.com/uploads/qe6bAu8fK165LTd9pa9Tmm/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: '3M and Solventum',
  logo: 'https://cdn.magicpatterns.com/uploads/8a94AChcQKnfAzckma8QZD/Screenshot_2026-08-27_at_12.40.13.png'
}, {
  name: 'AbbVie',
  logo: 'https://cdn.magicpatterns.com/uploads/pKJuSjA5sXAAftL8qfcZvo/Screenshot_2026-08-27_at_12.39.43.png'
}, {
  name: 'Healthcare company',
  logo: 'https://cdn.magicpatterns.com/uploads/aQbK55PqMKmQ6yF9dTcsFH/Screenshot_2026-08-20_at_21.22.20.png'
}] as const;
const REGIONS = ['Europe', 'Gulf', 'Africa', 'Asia', 'South America'] as const;
const MARKETS = ['Finland', 'Denmark', 'Germany', 'Poland', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy', 'Gulf', 'Morocco', 'Ivory Coast', 'Nigeria', 'South Africa', 'Kenya', 'Singapore', 'China', 'Peru', 'Brazil', 'Ireland', 'Sweden', 'Romania', 'Hungary'] as const;
export function SlideOperatingScale({}: SlideProps) {
  return <InteriorSlide surface data-id="element-371" data-mp="1t6rsweo109m">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-372" data-mp="1uhv6v478607">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-373" data-mp="avoax1z0xcse">
          Global delivery experience
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-374" data-mp="1m8idu2x6et0b">
          Built for continued campaigns—
          <span className="text-[var(--brand)]" data-id="element-375" data-mp="ex15t1apbpiy">already operating at scale.</span>
        </h2>
        <p className="mt-4 max-w-3xl text-[clamp(0.95rem,1.25vw,1.15rem)] leading-relaxed text-[var(--muted)]" data-id="element-376" data-mp="jvvlvvhaazzd">
          Continued campaign delivery is already being managed for leading
          healthcare companies across a broad international footprint.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-377" data-mp="su7jihgkiy1b">
        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-black/5 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-4" data-id="element-378" data-mp="3sdi8noibgtnw">
          <div className="flex items-start justify-between gap-4" data-id="element-379" data-mp="39l636swd9c9j">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-380" data-mp="39kxp4xoic0wr">
              <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-381" data-mp="3639ukvc3iqkj" />
            </div>
            <span className="text-[clamp(2.5rem,4vw,4rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-382" data-mp="360w28u8svsgo">
              5
            </span>
          </div>
          <h3 className="mt-4 text-xl font-bold text-[var(--fg)]" data-id="element-383" data-mp="2x18za40faeli">
            Companies with managed campaigns
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-384" data-mp="2s5lcfv9exxj6">
            Selected organizations supported through continued campaign delivery.
          </p>
          <div className="mt-5 grid flex-1 grid-cols-2 gap-3" data-id="element-385" data-mp="2icw3p858yxrj">
            {COMPANIES.map((company, index) => <figure key={company.name} className={`flex min-h-[4rem] items-center justify-center overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-2 ${index === COMPANIES.length - 1 ? 'col-span-2' : ''}`} data-id="element-386" data-mp="2dfe4i9h17wwq" data-mpi={index}>
                <img src={company.logo} alt={`${company.name} logo`} className="h-full max-h-14 w-full object-contain" data-id="element-387" data-mp="24fr0dfwx4czz" />
              </figure>)}
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="relative flex min-h-0 flex-col justify-between overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-3" data-id="element-388" data-mp="89qckgdlzg3m">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full border-[28px] border-white/10" data-id="element-389" data-mp="3krwzzmyfavj1" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-390" data-mp="36sx6o1zytsgj">
            <div className="h-6 w-6" aria-hidden="true" data-id="element-391" data-mp="3bqp0j1k67ond" />
          </div>
          <div className="relative" data-id="element-392" data-mp="1510wowtg4qnv">
            <p className="text-[clamp(4.5rem,8vw,7.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-393" data-mp="1522b9b9o7vac">
              22
            </p>
            <h3 className="mt-3 text-[clamp(1.25rem,2vw,1.75rem)] font-bold" data-id="element-394" data-mp="19y12d6mdvreg">
              Brands campaigned
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-395" data-mp="2pjbi6w2eyssq">
              Across different portfolios, lifecycle stages, and HCP needs.
            </p>
            <p className="mt-5 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-white/65" data-id="element-396" data-mp="349afkwm5a075">
              Continued delivery experience
            </p>
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-[var(--brand)]/10 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-5" data-id="element-397" data-mp="389ksyuc9nsw1">
          <div className="flex items-start justify-between gap-4" data-id="element-398" data-mp="1stu17kxzdkbq">
            <div className="flex items-center gap-3" data-id="element-399" data-mp="1ntzc9mxrn4tb">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-400" data-mp="1nvk77dnzsprp">
                <Globe2Icon className="h-5 w-5" aria-hidden="true" data-id="element-401" data-mp="2hjgkiyu2rmji" />
              </span>
              <div data-id="element-402" data-mp="2hl9schgfcct3">
                <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-403" data-mp="2mit3v9lqmbhc">
                  5
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-404" data-mp="2rj7g73udg7l6">
                  Regions covered
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-405" data-mp="30gzuc4ocx96z">
              <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-406" data-mp="359149jy1yntu">
                23
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-407" data-mp="3a72nc4vhan5b">
                Localizations
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-id="element-408" data-mp="3k5s2vqzwjvie">
            {REGIONS.map(region => <span key={region} className="rounded-full border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-3 py-1.5 text-xs font-bold text-[var(--brand)]" data-id="element-409" data-mp="18iyc68c1phyx">
                {region}
              </span>)}
          </div>

          <div className="mt-5 flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--surface)] p-4" data-id="element-410" data-mp="yjz4ot30nsdi">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--fg)]" data-id="element-411" data-mp="tmh5hueswrip">
              <MapPinIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-412" data-mp="1r9zf17gadxnr" />
              Market footprint
            </div>
            <ul className="grid flex-1 grid-cols-3 gap-x-4 gap-y-1.5" data-id="element-413" data-mp="1mav2c6u5lhso">
              {MARKETS.map(market => <li key={market} className="flex items-center gap-1.5 text-[0.68rem] font-medium leading-tight text-[var(--muted)]" data-id="element-414" data-mp="1hi1z5nec2hip">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-415" data-mp="1cipbj0fn16f2" />
                  {market}
                </li>)}
            </ul>
          </div>
        </motion.article>
      </div>
    </InteriorSlide>;
}